<template>
  <div class="cart-products-table">
    <p v-if="useEditMode" class="body-semi-bold">
      {{ tStatic('markedRows') }}: {{ selectedRows.length }} / {{ rows.length }}
    </p>
    <oie-action-wrapper v-if="useEditMode">
      <template v-if="rows.length" #left>
        <slot name="customMultiActions"></slot>

        <oie-my-machines-selector-slide-in
          :disabled="selectedRows.length === 0 ? true : false"
          context="mark-multiple-rows"
          :tooltip="tStatic('markRowsWithMachineTooltip')"
          :text="tStatic('markRowsWithMachine')"
          @select-owner="$emit('markSelectedRows', $event)"
          @select-machine="$emit('markSelectedRows', $event)"></oie-my-machines-selector-slide-in>
        <oie-base-action
          theme="destructive"
          :disabled="selectedRows.length === 0 ? true : false"
          @click="$emit('deleteSelectedRows')">
          <template #iconPrepend>
            <oie-base-icon icon="trash-can" size="xs"></oie-base-icon>
          </template>
          {{ tStatic('cartClearSelectedArticles') }}
        </oie-base-action>
      </template>
      <template #right>
        <oie-file-upload-shopping-list-slide-in
          v-if="!hideFileUpload"
          accept=".csv, .txt"
          aria-describedby="file-upload-instructions"
          @add-file-to-list="$emit('add-file-to-list', $event)">
          <p id="file-upload-instructions" class="body">
            {{ tStatic('uploadQuickOrderFileDescription') }}
          </p>
          <a
            class="body-blue"
            :href="tStatic('quickOrderUploadInstructionsExampleUrl')"
            target="_blank"
            rel="noopener">
            {{ tStatic('quickOrderUploadInstructionsExample') }}
          </a>
        </oie-file-upload-shopping-list-slide-in>
      </template>
    </oie-action-wrapper>
    <div class="cart-products-table__table-container">
      <table
        :class="[
          'product-rows',
          'cart-products-table__table',
          {
            'product-rows--use-multiselect':
              mq('to-medium') &&
              useEditMode
          }
        ]">
        <thead>
          <tr>
            <th v-if="useEditMode && rows.length" class="product-row__head-remove">
              <div class="product-row__head-inset">
                <oie-base-checkbox v-model="selectAll" :is-for="`toggle-all`" :text=" mq('to-medium') ? tStatic('cartProductHeaderSelectAllLabel'): null"></oie-base-checkbox>
              </div>
            </th>

            <th class="product-row__head-image"></th>

            <th class="product-row__head-article-name">
              <div class="product-row__head-inset">
                <span>
                  {{ tStatic("cartProductHeaderLabel") }}
                </span>
              </div>
            </th>

            <th class="product-row__head-stock-status">
              <div class="product-row__head-inset">
                {{ tStatic("cartStatusSendNowLabel") }}
              </div>
            </th>

            <th class="product-row__head-stock-status">
              <div class="product-row__head-inset">
                {{ tStatic("cartStatusSendLaterLabel") }}
              </div>
            </th>

            <th :class="{ 'product-row__head-quantity': useEditMode }">
              <div class="product-row__head-inset">
                {{ tStatic("cartQuantityLabel") }}
              </div>
            </th>

            <th class="product-row__head-comment">
              <div class="product-row__head-inset">
                {{ tStatic("cartRowCommentLabel") }}
              </div>
            </th>

            <th v-if="hasMyMachines" class="product-row__head-machine">
              <div class="product-row__head-inset">
                {{ tStatic("cartRowMachineLabel") }}
              </div>
            </th>

            <th class="product-row__head-item-price">
              <div
                class="product-row__head-inset product-row__head-inset--align-right">
                <span>{{ tStatic("cartRowPriceLabel") }}</span>
              </div>
            </th>

            <th class="product-row__head-total-price">
              <div
                class="product-row__head-inset product-row__head-inset--align-right">
                <span>{{ tStatic("cartRowPriceTotalLabel") }}</span>
              </div>
            </th>

            <th v-if="useEditMode" class="product-row__head-remove"></th>
          </tr>
        </thead>

        <tbody>
          <tr
            v-if="rows && rows.length && mq('medium')"
            aria-hidden="true"
            class="cart-products__row-spacer">
            <td :colspan="getColumnSpans"></td>
          </tr>
          <template v-for="(row, i) of rows">
            <tr :key="`table-row-${rowIdentification(row)}`" :class="[{'cart-products__row-has-cross-sell--first-row': hasCartItemCrossSell(row)}]">
              <td v-if="useEditMode">
                <div class="product-row__body-inset product-row__body-inset--select">
                  <oie-base-checkbox
                    v-model="selectedRowsProxy"
                    :is-for="rowIdentification(row)"
                    :val="rowIdentification(row)"></oie-base-checkbox>
                </div>
              </td>

              <td class="product-row__body-td--image">
                <div class="product-row__body-inset product-row__body-inset--article">
                  <oie-base-image
                    v-if="row.image || get(row, 'item.image')"
                    :images="`${getAssetPath(row.image) || getAssetPath(get(row, 'item.image'))}?preset=ProductThumb&watermark=OIEWatermarkSmall`"
                    :alt-text="row.displayName"
                    image-width="3.75rem"
                    image-height="3.75rem"
                    class="product-row__image"></oie-base-image>

                  <oie-base-image
                    v-else
                    :images="getAssetPath('/globalassets/missing404/404.png?preset=ProductThumb')"
                    :alt-text="row.displayName"
                    image-width="3.75rem"
                    image-height="3.75rem"
                    class="product-row__image"></oie-base-image>
                </div>
              </td>

              <td class="product-row__body-td--article">
                <div class="product-row__body-inset product-row__body-inset--article">
                  <a
                    v-if="useEditMode && (row.url || row.item.url) && !disableInternalItem(row)"
                    :href="row.url || row.item.url"
                    class="product-row__name">
                    {{ row.displayName }}
                  </a>

                  <div v-else class="product-row__name">
                    {{ row.displayName }}
                  </div>

                  <div class="product-row__article-number">
                    {{ `${tStatic("headerCartItemNumberLabel")} ${row.itemId}` }}
                  </div>
                </div>
              </td>

              <td>
                <div v-if="mq('to-medium')" class="product-row__body-header">
                  {{ tStatic("cartStatusSendNowLabel") }}
                </div>

                <div v-if="!isQuoteItem(row)" class="product-row__body-inset">
                  <oie-product-status
                    v-if="row.stock <= 0"
                    status="in-stock"
                    text="0"
                    size="medium"></oie-product-status>

                  <oie-product-status
                    v-else-if="row.stock - row.quantity > 0"
                    status="in-stock"
                    :text="calculateStock(0, row.quantity)"
                    size="medium"></oie-product-status>

                  <oie-product-status
                    v-else-if="row.stock <= row.quantity"
                    status="in-stock"
                    :text="calculateStock(row.stock, 0)"
                    size="medium"></oie-product-status>
                </div>
              </td>

              <td>
                <div v-if="mq('to-medium')" class="product-row__body-header">
                  {{ tStatic("cartStatusSendLaterLabel") }}
                </div>

                <div v-if="!isQuoteItem(row)" class="product-row__body-inset">
                  <oie-product-status
                    v-if="row.stock < 0"
                    status="few-stock"
                    :text="`${row.quantity}`"
                    size="medium"></oie-product-status>

                  <oie-product-status
                    v-else
                    status="few-stock"
                    :text="calculateStock(row.stock, row.quantity)"
                    size="medium"></oie-product-status>
                </div>
              </td>

              <td>
                <div v-if="mq('to-medium')" class="product-row__body-header">
                  {{ tStatic("cartQuantityLabel") }}
                </div>

                <div class="product-row__body-inset">
                  <oie-product-quantity
                    v-if="useEditMode && !disableInternalItem(row)"
                    :cart-row="row"
                    :stop-quantity-events="true"
                    @input="($event) => $emit('updateQuantity', {row: row.id, quantity: $event})"></oie-product-quantity>

                  <template v-else>
                    {{ row.quantity }}
                  </template>
                </div>
              </td>

              <td class="product-row__td--comment">
                <div v-if="mq('to-medium')" class="product-row__body-header">
                  {{ tStatic("cartRowCommentLabel") }}
                </div>

                <div class="product-row__body-inset">
                  <!-- COMMENT -->
                  <oie-comment
                    :title="tStatic('headerCartNotePlaceholder')"
                    :initial-value="row.comment"
                    :use-edit-mode="useEditMode"
                    @save="(...args) => updateComment(row, ...args)"
                    @delete="(...args) => updateComment(row, '', ...args)"></oie-comment>

                  <!-- ASSEMBLY NOTE -->
                  <oie-comment
                    v-if="isSuperUser"
                    :title="tStatic('headerCartAssemblyNotePlaceholder')"
                    :initial-value="row.assemblyNote"
                    :use-edit-mode="useEditMode"
                    class="cart-assemblynote"
                    @save="(...args) => updateAssemblyNote(row, ...args)"
                    @delete="(...args) => updateAssemblyNote(row, '', ...args)"></oie-comment>
                </div>
              </td>

              <td v-if="hasMyMachines" class="product-row__td--machine">
                <div v-if="mq('to-medium')" class="product-row__body-header">
                  {{ tStatic("cartRowMachineLabel") }}
                </div>

                <div class="product-row__body-inset">
                  <div v-if="useEditMode" class="product-row__td--machine-actions">
                    <oie-my-machines-selector-slide-in
                      :context="row.id"
                      :tooltip="row.machineOwnerId || row.machineId ? tStatic('changeMachineTooltip'): ''"
                      :text="row.machineOwnerId || row.machineId ? tStatic('changeMachine') : tStatic('selectMachine')"
                      @select-owner="updateMachineOrOwner($event, row)"
                      @select-machine="updateMachineOrOwner($event, row)"></oie-my-machines-selector-slide-in>
                    <oie-base-action
                      v-if="row.machineId || row.machineOwnerId"
                      v-tooltip.right="tStatic('clearMachineTooltip')"
                      class="product-row__td--machine-actions-cancel"
                      theme="destructive"
                      @click="updateMachineOrOwner({}, row)">
                      <template #iconPrepend>
                        <oie-base-icon icon="close" size="s"></oie-base-icon>
                      </template>
                    </oie-base-action>
                  </div>
                  <template v-if=" row.machineId || row.machineOwnerId">
                    <p v-if="row.machineOwnerId" class="product-row__td--machine-info">
                      <oie-base-icon icon="user-avatar" size="s"></oie-base-icon>{{ machineOwnerDictionary[row.machineOwnerId] }}
                    </p>
                    <p v-if="row.machineId" class="product-row__td--machine-info">
                      <oie-base-icon icon="machine" size="s"></oie-base-icon> {{ machineDictionary[row.machineId] }}
                    </p>
                  </template>
                  <p v-else class="product-row__td--machine-info">
                    {{ `(${tStatic('noMachineSelected')})` }}
                  </p>
                </div>
              </td>

              <td>
                <div v-if="mq('to-medium')" class="product-row__body-header">
                  {{ tStatic("cartRowPriceLabel") }}
                </div>

                <div
                  v-if="!isQuoteItem(row)"
                  class="product-row__body-inset product-row__body-inset--align-right product-row__body-inset--price"
                  :class="row.isDiscounted ? 'discounted' : ''">
                  <div v-if="!isRowInPriceEditMode(row.id)">
                    <div style="display: flex; gap: 5px;">
                      {{ formatPrice(row.price) }}
                      <button v-if="isSuperUser && enablePriceEdit" class="icon-button" @click="togglePriceEdit(row.id)">
                        <oie-use-svg svg="pen"></oie-use-svg>
                      </button>
                    </div>
                  </div>
                  <template v-else>
                    <oie-cart-superuser-change-price
                      :cart-row="row"
                      @save-new-price="($event) => {
                        $emit('updatePrice', {row: row.id, price: $event})
                        togglePriceEdit(row.id)
                      }"
                      @close="togglePriceEdit($event)"></oie-cart-superuser-change-price>
                  </template>
                  <template v-if="isSuperUser && enablePriceEdit">
                    <p v-if="row.purchasePrice" class="product-row__body-purchase-price">
                      {{ tStatic("superUserPurchasePrice") }} {{ formatPrice(row.purchasePrice) }}
                    </p>
                    <p v-if="row.tb" class="product-row__body-purchase-price">
                      {{ tStatic("superUserCartRowTb") }} {{ formatPrice(row.tb) }}
                    </p>
                    <p v-if="row.tg" class="product-row__body-purchase-price">
                      {{ tStatic("superUserCartRowTg") }} {{ `${row.tg}%` }}
                    </p>
                  </template>
                </div>
              </td>

              <td>
                <div v-if="mq('to-medium')" class="product-row__body-header">
                  {{ tStatic("cartRowPriceTotalLabel") }}
                </div>

                <div
                  v-if="!isQuoteItem(row)"
                  class="product-row__body-inset product-row__body-inset--align-right product-row__body-inset--price">
                  {{ formatPrice(row.price * row.quantity) }}
                </div>
              </td>
              <td v-if="useEditMode">
                <div class="product-row__body-inset product-row__body-inset--remove">
                  <oie-base-button
                    css-classes="product-row__remove-button"
                    @click="$emit('deleteRow', {row: row.id})"></oie-base-button>
                </div>
              </td>
            </tr>
            <tr v-if="isQuoteItem(row)" :key="`table-row-quote-info-${rowIdentification(row)}`" class="product-row--quote-info">
              <td :colspan="getColumnSpans">
                <div
                  v-html="tStatic('requestQuoteContent')"></div>
              </td>
            </tr>
            <!-- MANDATORY COMMENT -->
            <tr v-if="row.mandatoryComment && row.mandatoryComment.length" :key="`table-row-mandatory-comment-${rowIdentification(row)}`" class="product-row--mandatory-comment">
              <td :colspan="getColumnSpans">
                <div>
                  <div>
                    <span v-for="comment in row.mandatoryComment" :key="comment.key">
                      <span>{{ `${comment.title}: ` }}</span>{{ `${comment.value}` }}
                    </span>
                  </div>
                  <oie-base-action v-if="useEditMode" theme="skeleton" @click="editMandatoryComment(row)">
                    {{ tStatic('editMandatoryCommentLabel') }}
                    <template #iconAppend>
                      <oie-base-icon icon="edit" size="xs"></oie-base-icon>
                    </template>
                  </oie-base-action>
                </div>
              </td>
            </tr>
            <!-- CROSSELL -->
            <tr v-if="hasCartItemCrossSell(row)" :key="`table-row-crosssell-${rowIdentification(row)}`" class="cart-products__row-has-cross-sell--second-row">
              <td :colspan="getColumnSpans">
                <div style="width: 100%">
                  <oie-expandable-item
                    :heading="tStatic('cartCartItemCrossSellTitle')"
                    :open-heading="tStatic('cartCartItemCrossSellTitleOpen').replace('{0}', row.displayName)"
                    theme="secondary"
                    @toggle-accordion="handleToggleCrossSell($event)">
                    <oie-product-list-page-wrapper
                      ref="productList"
                      style="flex-grow: 1;"
                      :remember-scroll="false"
                      :show-matching-filter="false"
                      :use-compare="false"
                      :related-products="getCrossSellIds(row.crossSellProducts)"
                      :hide-show-more-link="true"
                      :show-filter="false"
                      :use-block="true"
                      :show-sort-order="false"
                      :page-size="getCrossSellIds(row.crossSellProducts).length"
                      :use-grid="mq('to-medium')"
                      :ignore-related="true"
                      :type-of-listing="listTypes.SPARE_PARTS_CROSS_SELL">
                    </oie-product-list-page-wrapper>
                  </oie-expandable-item>
                </div>
              </td>
            </tr>
            <!-- SPACER -->
            <tr
              v-if="rows && i < (rows.length - 1)"
              :key="`table-row-spacer-${rowIdentification(row)}`"
              aria-hidden="true"
              class="cart-products__row-spacer">
              <td :colspan="getColumnSpans"></td>
            </tr>
          </template>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import _ from 'lodash'
import { listTypes } from '@/core/analytics-variables'
import { showMandatoryCommentModal } from '@/core/modal-service'
import { productStatuses } from '@/variables/product-statuses'

export default {
  props: {
    rows: {
      type: Array,
      default: () => []
    },
    useEditMode: {
      type: Boolean,
      default: true
    },
    enablePriceEdit: {
      type: Boolean,
      default: false
    },
    selectedRows: {
      type: Array,
      default: () => []
    },
    // temporary prop, we will implement file upload also in cart view in near future, then useEditMode will be only prop needed
    hideFileUpload: {
      type: Boolean,
      default: false
    }
  },
  data: () => ({
    rowsInPriceEditMode: [],
    listTypes,
    selectAll: false
  }),
  computed: {
    ...mapGetters(['enableGtm']),
    ...mapGetters('myMachines', ['myMachines', 'machineOwnerDictionary', 'machineDictionary']),
    selectedRowsProxy: {
      get() {
        return this.selectedRows
      },
      set(value) {
        this.$emit('update:selectedRows', value)
      }
    },
    getColumnSpans: {
      get() { return this.myMachines ? '11' : '10' }
    },
    isSuperUser() {
      return (
        this.clientModel.impersonating !== null || this.clientModel.isSuperUser
      )
    },
    hasMyMachines() {
      return this.myMachines && (this.myMachines.machineOwners.length > 0 || this.myMachines.machines.length > 0)
    }
  },
  watch: {
    selectAll(newVal) {
      if(newVal) {
        this.selectedRowsProxy = this.rows.map(row => row.id)
      } else {
        this.selectedRowsProxy = []
      }
    }
  },
  methods: {
    ...mapActions('myMachines', ['getMyMachines']),
    calculateStock(stock, quantity) {
      if (stock === 0) {
        return quantity.toString()
      }
      if (stock > quantity && quantity > 0) {
        return (0).toString()
      }
      if (quantity === 0) {
        return stock.toString()
      }

      return (quantity - stock).toString()
    },
    rowIdentification(row) {
      return row.id
    },
    async updateMachineOrOwner($event, row) {
      this.$emit('updateMachineOrOwner', { row: row.id, machineOrOwner: $event })
    },
    getCrossSellIds(items) {
      return _.map(items, item => item.id)
    },
    hasCartItemCrossSell(row) {
      return row.crossSellProducts && row.crossSellProducts.length > 0
    },
    handleToggleCrossSell(isOpen) {
      window.dispatchEvent(new Event('resize'))
    },
    async editMandatoryComment(row) {
      const updatedComments = await showMandatoryCommentModal({ mandatoryComment: { [row.itemId]: row.mandatoryComment }, title: this.tStatic('editMandatoryComment') })
      this.$emit('updateMandatoryComment', { row: row.id, mandatoryComment: updatedComments[row.itemId] })
    },
    updateComment(row, comment, callback) {
      this.$emit('updateComment', { row: row.id, comment, callback })
    },
    updateAssemblyNote(row, comment, callback) {
      this.$emit('updateAssemblyNote', { row: row.id, comment, callback })
    },
    togglePriceEdit(id) {
      this.rowsInPriceEditMode = _.xor(this.rowsInPriceEditMode, [id])
    },
    isRowInPriceEditMode(id) {
      return this.rowsInPriceEditMode.indexOf(id) >= 0
    },
    isQuoteItem(row) {
      return row.publicationStatusCode === productStatuses.noPrice
    },
    disableInternalItem(row) {
      const isInternalItem = row.publicationStatusCode === productStatuses.internal
      return isInternalItem && !this.isSuperUser
    }
  }
}
</script>

<style lang="scss">
.product-rows {
  display: block;
  width: 100%;

  @include media(medium) {
    display: table;
    border-collapse: collapse;
    border: 1px solid color(border-gray);
    background-color: color(background-white);
  }

  @media print {
    display: table;
    border-collapse: collapse;
    border: 1px solid color(border-gray);
    background-color: color(background-white);
  }
}

.grid--list .product-rows {
  @include media(medium) {
    border-top: 0;
  }
}

.product-rows thead {
  display: none;
  background-color: color(background-blue-6);

  @include media(medium) {
    display: table-header-group;
  }

  @media print {
    display: table-header-group;
  }
}

.product-rows--use-multiselect thead th:not(.product-row__head-remove) {
  display: none;
}

.product-rows--use-multiselect thead,
.product-rows--use-multiselect thead tr {
  display: block;
  background-color: transparent;
  padding-left: 1px;
}

.product-rows--use-multiselect thead th.product-row__head-remove {
  display: block;
  width: 100%;
  height: auto;

  > div {
    padding-left: 0;
  }
}

.product-rows tbody {
  @include media(to-medium) {
    display: block;
    width: 100%;
  }

  @media print {
    display: table-row-group;
    width: 100%;
  }
}

.product-rows tbody tr {
  @include media(to-medium) {
    display: block;
    width: 100%;
  }

  @media print {
    display: table-row;
    width: 100%;
  }
}

.product-rows tbody tr {
  @include media(to-medium) {
    border-right: 1px solid color(border-gray);
    border-left: 1px solid color(border-gray);
    background-color: color(background-white);
  }
}

.product-rows th,
.product-rows td {
  margin: 0;
  padding: 0;

  @media print {
    &:first-child {
      display: none;
    }
  }
}

.product-rows th {
  height: 3.375rem;
}

.product-rows td {
  display: flex;
  flex-direction: row;

  @include media(to-medium) {
    border-bottom: 1px solid color(border-gray);
  }

  @include media(medium) {
    display: table-cell;
    min-height: 6.25rem;
  }

  @media print {
    display: table-cell;
  }
}

.product-rows--order-history td {
  @include media(to-medium) {
    flex-direction: row;
  }
}

.product-rows--order-history td.product-row__td--comment {
  @include media(to-medium) {
    flex-direction: column;
  }
}

.product-rows tbody tr {
  border-top: 1px solid color(border-gray);
}

.product-rows.cart-products-table__table
  tr:hover td {
  background-color: initial !important;
}

.product-rows td.product-row__body-td--checkbox {
  border-bottom: 0;
}

.product-rows td.product-row__body-td--image {
  border-bottom: 0;
}

.product-rows td.product-row__td--comment {
  flex-direction: column;
}

/* table head */

.product-row__head-inset {
  @include body-smaller;
  position: relative;
  display: flex;
  align-items: flex-end;
  padding: measure(quarter);
  height: 100%;
}

.product-row__head-inset--align-right {
  justify-content: flex-start;

  @include media(medium) {
    justify-content: flex-end;
  }
}

.product-row__head-inset > span {
  pointer-events: none;
}

.product-row__head-inset:hover .table-sort {
  opacity: 1;
  visibility: visible;
}

.product-row__head-image {
  width: 6.25rem;
}

.product-row__head-article-name {
  width: 15rem;
}

.product-row__head-stock-status {
  width: 12.5rem;
}

.product-row__head-quantity {
  width: 8.125rem;
}

.product-row__head-comment {
  min-width: 16.875rem;
}

.product-row__head-item-price {
  width: 10.625rem;
}

.product-row__head-total-price {
  width: 8.4375rem;
}

.product-row__head-remove {
  width: 2.5rem;

  @media print {
    display: none;
  }
}

// .product-rows thead tr th {
//   width: auto;
// }

.product-row__head-order-number {
  width: 14.0625rem;
}

.product-row__head-order-date {
  width: 12.25rem;
}

.product-row__head-product-amount {
  width: 10rem;
}

.product-row__head-total-sum {
  width: 10.4375rem;
}

/* table body */

.product-row__body-inset {
  @include body-smaller;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  margin-left: auto;
  padding: measure(quarter);
  height: 100%;

  @include media(medium) {
    min-height: 6.25rem;
  }
}

.product-rows--order-history .product-row__body-inset {
  @include media(to-medium) {
    margin-left: auto;
  }
}

.product-row__body-td--article {
  @media print {
    width: 100%;
  }
}

.product-row__body-td--article .product-row__body-inset {
  margin-left: 0;
}

.product-row__body-td--checkbox .product-row__body-inset {
  margin-left: 0;
}

.product-row__td--comment .product-row__body-inset {
  margin-left: 0;
}

.product-row__body-td--image .product-row__body-inset {
  margin-left: 0;
}

.product-row__body-inset--align-right {
  align-items: flex-end;
}

.product-row__body-inset--price {
  @include body-smaller;

  @include media(medium) {
    @include body-semi-bold;
  }
}

.product-row__body-inset--bold {
  @include body-semi-bold;
}

.product-row__body-inset--select {
  justify-content: center;
  margin: 0;
}

.product-row__body-inset--article {
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
}

.product-row__body-inset--remove {
  @include media(to-medium) {
    align-items: center;
  }
}

.product-row__body-header {
  @include body-smaller-semi-bold;
  padding: measure(quarter);

  @include media(medium) {
    display: none;
  }

  @media print {
    display: none;
  }
}

.product-row__body-purchase-price {
  margin-top: measure(quarter);
  font-size: 13px;
  font-weight: 400;
  white-space: nowrap;
}

.product-row__name {
  @include body-smaller-semi-bold;
  color: color(text-blue);
  text-decoration: none;
  margin: 0 0 measure(eighth) 0;
  padding: 0;

  &:hover {
    text-decoration: underline;
  }

  @include media(medium) {
    @include body-semi-bold;
    color: color(text);
  }
}

.product-rows--readonly .product-row__name:hover {
  text-decoration: none;
}

.product-row__article-number {
  @include body-smaller;
}

.product-row__order-number {
  @include body-semi-bold;
  color: color(text-blue);
  margin: 0;
  padding: 0;
}

.product-tabs__variants .product-rows tbody tr {
  @include media(to-medium) {
    border-right: none;
    border-left: none;
  }
}

.product-row__image {
  height: 3.75rem;
  width: 3.75rem;
}

.product-row__remove-button {
  @include reset-button;
  @include background-svg(trashbin);
  width: 1.0625rem;
  height: 1.25rem;
  padding: 0;
}

.product-row__remove-button > svg {
  width: 100%;
  height: 100%;
}

.product-row__comment-textarea {
  flex-grow: 1;
  width: 100%;
  padding: measure(quarter);
  background-color: #fafafa;
  border: none;
  box-shadow: 0 0 4px 0 rgba(170, 170, 170, 0.5);
  resize: none;
}

.product-row__comment-edit-done {
  background-color: transparent;
  border: none;
  margin: 0;
  padding: 0;
  position: absolute;
  top: measure(half);
  right: measure(half);
  width: 1.25rem;
  height: 1.25rem;
}

.product-row__comment-edit-done > svg {
  width: 100%;
  height: 100%;
}

.cart-assemblynote {
  margin-top: measure(quarter);
}

.discounted {
  color: red;
}

// Cross-sell styles BEGIN

.product-rows tbody tr.cart-products__row-has-cross-sell--first-row {
  @include media(to-medium) {
    margin-bottom: 0;
  }
}

.cart-products__row-spacer {
  border-left: 1px solid #F6F6F6 !important;
  border-right: 1px solid #F6F6F6 !important;
}

.cart-products__row-spacer td{
  background-color: #F6F6F6;
  border-left: 1px solid #F6F6F6 !important;
  border-right: 1px solid #F6F6F6 !important;
  border-top: none !important;
  border-bottom: none !important;
  height: measure(quarter);
}

// Cross-sell styles END

// My machine related styles
.product-row__td--machine-actions {
  display: flex;
  gap: 0.25rem;
}

.product-row__td--machine-actions-cancel {
  padding: 3px;

  > svg:first-child:not(:only-child) {
    margin-right: 0;
  }
}

.product-row__td--machine-info {
  display: flex;
  align-items: center;
  padding: measure(eighth) measure(quarter) measure(eighth) measure(quarter);
}

.product-row__td--machine-info>:first-child {
  margin-right: measure(eighth);
}

// Mandatory comment styles BEGIN
.product-row--mandatory-comment,
.product-row--quote-info {
  position: relative;
  background-color: color(background-yellow) !important;
  border: 1px solid color(border-yellow) !important;
}

.product-row--mandatory-comment td,
.product-row--quote-info td {
  @include body;
  padding: measure(quarter);
}

.product-row--mandatory-comment td > div {
  display: flex;
  align-items: center;
  gap: measure(quarter);
}

.product-row--mandatory-comment td div > span > span {
  @include body-semi-bold;
}

.product-row--mandatory-comment .base-action {
  position: static;
  text-decoration: underline;
}

.product-row--mandatory-comment .base-action::after {
  content: '';
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
}
// Mandatory comment styles END

.cart-products-table >*:not(:last-child) {
  margin-bottom: measure(quarter);
}

.cart-products-table__table-container {
  overflow: auto;
  max-height: 70vh;
}
</style>
